import instance from '../../../api/axios'
import { uploadFile } from '../../Files/api/uploadFile'
import removeFile from '../../Files/api/removeFile'
import {
  AnalyzedBigPurchase,
  AnalyzedDailyPurchase,
  AnalyzedGasBill,
  AnalyzedWaterBill,
  AnalyzedElectricityBill,
  BigPurchase,
  CreateBigPurchase,
  CreateDailyPurchase,
  CreateUtilityBill,
  DailyPurchase,
  UpdateBigPurchase,
  UpdateDailyPurchase,
  UpdateUtilityBill,
  UtilityBill,
  UtilityBillDocumentFile,
} from '../types/purchase'

export const loadDailyPurchases = async (): Promise<DailyPurchase[]> => {
  const response = await instance.get('/purchases/daily')
  return response.data
}

export const saveDailyPurchase = async (
  purchase: CreateDailyPurchase,
): Promise<DailyPurchase> => {
  const response = await instance.post('/purchases/daily', purchase)
  return response.data
}

export const modifyDailyPurchase = async (
  purchase: UpdateDailyPurchase,
): Promise<DailyPurchase> => {
  const response = await instance.put('/purchases/daily', purchase)
  return response.data
}

export const removeDailyPurchase = async (id: string): Promise<string> => {
  const response = await instance.delete(`/purchases/daily/${id}`)
  return response.data
}

export const updateDailyProductType = async (
  productId: string,
  productType: string,
  remember = true,
): Promise<void> => {
  await instance.put(`/purchases/daily/products/${productId}/type`, {
    productType,
    remember,
  })
}

export const classifyComparisonProduct = async (
  productId: string,
): Promise<{ baseProductName: string }> => {
  const response = await instance.post(
    `/purchases/comparison/products/${productId}/classify`,
  )
  return response.data
}

export const analyzeDailyReceipt = async (
  file: File,
): Promise<AnalyzedDailyPurchase> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await instance.post(
    '/purchases/daily/analyze-receipt',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export const loadBigPurchases = async (): Promise<BigPurchase[]> => {
  const response = await instance.get('/purchases/big')
  return response.data
}

export const saveBigPurchase = async (
  purchase: CreateBigPurchase,
): Promise<BigPurchase> => {
  const response = await instance.post('/purchases/big', purchase)
  return response.data
}

export const modifyBigPurchase = async (
  purchase: UpdateBigPurchase,
): Promise<BigPurchase> => {
  const response = await instance.put('/purchases/big', purchase)
  return response.data
}

export const removeBigPurchase = async (id: string): Promise<string> => {
  const response = await instance.delete(`/purchases/big/${id}`)
  return response.data
}

export const analyzeBigPurchaseImage = async (
  file: File,
): Promise<AnalyzedBigPurchase> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await instance.post(
    '/purchases/big/analyze-image',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export const analyzeGasBill = async (
  files: File | File[],
): Promise<AnalyzedGasBill> => {
  const formData = new FormData()
  const list = (Array.isArray(files) ? files : [files]).filter(Boolean)
  list.forEach((file) => formData.append('files', file))

  const response = await instance.post(
    '/purchases/utilities/analyze-gas-bill',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export const analyzeWaterBill = async (
  files: File | File[],
): Promise<AnalyzedWaterBill> => {
  const formData = new FormData()
  const list = (Array.isArray(files) ? files : [files]).filter(Boolean)
  list.forEach((file) => formData.append('files', file))

  const response = await instance.post(
    '/purchases/utilities/analyze-water-bill',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export const analyzeElectricityBill = async (
  files: File | File[],
): Promise<AnalyzedElectricityBill> => {
  const formData = new FormData()
  const list = (Array.isArray(files) ? files : [files]).filter(Boolean)
  list.forEach((file) => formData.append('files', file))

  const response = await instance.post(
    '/purchases/utilities/analyze-electricity-bill',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export const loadUtilityBills = async (): Promise<UtilityBill[]> => {
  const response = await instance.get('/purchases/utilities')
  return response.data
}

const toUtilityBillBody = (
  bill: CreateUtilityBill | UpdateUtilityBill,
): CreateUtilityBill | UpdateUtilityBill => {
  const body: CreateUtilityBill = {
    category: bill.category,
    billDate: bill.billDate,
    amount: bill.amount,
    currency: bill.currency,
  }

  const optionalFields: (keyof CreateUtilityBill)[] = [
    'provider',
    'notes',
    'name',
    'description',
    'bankName',
    'creditPercentage',
    'monthlyQuota',
    'totalToPay',
    'periodStart',
    'periodEnd',
    'creditType',
    'monthlyQuotas',
    'counterM3',
    'monthSpendM3',
    'previousCounterM3',
    'conversionFactorKwh',
    'energyKwh',
    'invoiceNumber',
    'meterNumber',
    'cups',
    'supplyAddress',
    'postalCode',
    'city',
    'documentFileId',
    'documentFileName',
    'documentMimeType',
    'documentFiles',
  ]

  optionalFields.forEach((field) => {
    if (bill[field] !== undefined) {
      body[field] = bill[field] as never
    }
  })

  if ('id' in bill && bill.id) {
    return { ...body, id: bill.id }
  }

  return body
}

export const saveUtilityBill = async (
  bill: CreateUtilityBill,
): Promise<UtilityBill> => {
  const response = await instance.post(
    '/purchases/utilities',
    toUtilityBillBody(bill),
  )
  return response.data
}

export const modifyUtilityBill = async (
  bill: UpdateUtilityBill,
): Promise<UtilityBill> => {
  const response = await instance.put(
    '/purchases/utilities',
    toUtilityBillBody(bill),
  )
  return response.data
}

export const removeUtilityBill = async (id: string): Promise<string> => {
  const response = await instance.delete(`/purchases/utilities/${id}`)
  return response.data
}

const toUpdateUtilityBill = (bill: UtilityBill): UpdateUtilityBill => ({
  id: bill.id,
  category: bill.category,
  billDate: bill.billDate,
  amount: bill.amount,
  currency: bill.currency,
  provider: bill.provider || undefined,
  notes: bill.notes || undefined,
  name: bill.name || undefined,
  description: bill.description || undefined,
  bankName: bill.bankName || undefined,
  creditPercentage: bill.creditPercentage ?? undefined,
  monthlyQuota: bill.monthlyQuota ?? undefined,
  totalToPay: bill.totalToPay ?? undefined,
  periodStart: bill.periodStart || undefined,
  periodEnd: bill.periodEnd || undefined,
  creditType: bill.creditType || undefined,
  monthlyQuotas: bill.monthlyQuotas || undefined,
  counterM3: bill.counterM3 ?? undefined,
  monthSpendM3: bill.monthSpendM3 ?? undefined,
  previousCounterM3: bill.previousCounterM3 ?? undefined,
  conversionFactorKwh: bill.conversionFactorKwh ?? undefined,
  energyKwh: bill.energyKwh ?? undefined,
  invoiceNumber: bill.invoiceNumber || undefined,
  meterNumber: bill.meterNumber || undefined,
  cups: bill.cups || undefined,
  supplyAddress: bill.supplyAddress || undefined,
  postalCode: bill.postalCode || undefined,
  city: bill.city || undefined,
  documentFileId: bill.documentFileId || undefined,
  documentFileName: bill.documentFileName || undefined,
  documentMimeType: bill.documentMimeType || undefined,
  documentFiles: bill.documentFiles || undefined,
})

const uniqueStrings = (values: Array<string | null | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value))))

const getBillDocumentIds = (bill: UtilityBill): string[] =>
  uniqueStrings([
    ...(bill.documentFiles || []).map((file) => file.id),
    bill.documentFileId,
  ])

const toDocumentFiles = (
  files: { id: string; name?: string; type?: string }[],
): UtilityBillDocumentFile[] =>
  files.map((file) => ({
    id: file.id,
    name: file.name,
    mimeType: file.type,
  }))

const stripDocumentChange = <
  T extends {
    documentFile?: File
    uploadFiles?: File[]
    removeDocument?: boolean
  },
>(
  payload: T,
): Omit<T, 'documentFile' | 'uploadFiles' | 'removeDocument'> => {
  const {
    documentFile: _documentFile,
    uploadFiles: _uploadFiles,
    removeDocument: _removeDocument,
    ...bill
  } = payload
  return bill
}

export const syncUtilityBillDocument = async (
  bill: UtilityBill,
  options: { file?: File; files?: File[]; remove?: boolean } = {},
): Promise<UtilityBill> => {
  const previousIds = getBillDocumentIds(bill)
  const incoming = (
    options.files?.length ? options.files : options.file ? [options.file] : []
  ).filter(Boolean)

  if (incoming.length) {
    const uploaded: Array<{ id: string; name?: string; type?: string }> = []
    for (const file of incoming) {
      uploaded.push(
        await uploadFile({
          file,
          name: file.name,
          description: '',
          isPublic: false,
          utilityBillId: bill.id,
        }),
      )
    }

    const primary = uploaded[0]
    const updated = await modifyUtilityBill({
      ...toUpdateUtilityBill(bill),
      documentFileId: primary.id,
      documentFileName: primary.name || incoming[0].name,
      documentMimeType: primary.type || incoming[0].type,
      documentFiles: toDocumentFiles(uploaded),
    })

    await Promise.all(
      previousIds
        .filter((id) => !uploaded.some((file) => file.id === id))
        .map((id) => removeFile(id).catch(() => undefined)),
    )

    return updated
  }

  if (options.remove && previousIds.length) {
    const updated = await modifyUtilityBill({
      ...toUpdateUtilityBill(bill),
      documentFileId: undefined,
      documentFileName: undefined,
      documentMimeType: undefined,
      documentFiles: [],
    })

    await Promise.all(
      previousIds.map((id) => removeFile(id).catch(() => undefined)),
    )

    return updated
  }

  return bill
}

export const saveUtilityBillWithDocument = async (
  payload: CreateUtilityBill & {
    documentFile?: File
    uploadFiles?: File[]
    removeDocument?: boolean
  },
): Promise<UtilityBill> => {
  const saved = await saveUtilityBill(stripDocumentChange(payload))
  if (!saved?.id) {
    throw new Error('Utility bill was not saved')
  }

  return syncUtilityBillDocument(saved, {
    file: payload.documentFile,
    files: payload.uploadFiles,
    remove: payload.removeDocument,
  })
}

export const modifyUtilityBillWithDocument = async (
  payload: UpdateUtilityBill & {
    documentFile?: File
    uploadFiles?: File[]
    removeDocument?: boolean
  },
): Promise<UtilityBill> => {
  const saved = await modifyUtilityBill(stripDocumentChange(payload))
  if (!saved?.id) {
    throw new Error('Utility bill was not saved')
  }

  return syncUtilityBillDocument(saved, {
    file: payload.documentFile,
    files: payload.uploadFiles,
    remove: payload.removeDocument,
  })
}
